import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatTimecode, parseTimecode } from "./timecode";

/**
 * The elapsed time of the player, editable in place: click it, type a timecode
 * ("12", "12:30", "1:05:30") and press Enter to jump straight there instead of
 * dragging the progress bar.
 */
const SeekableTime = ({
  timeInSeconds,
  durationInSeconds,
  onSeek,
  isDisabled = false,
  className = "",
  inputClassName = "",
}) => {
  const { t } = useTranslation();

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);
  // A ref, not state: it is never rendered, and `commit` must read it
  // synchronously when the field is being torn down.
  const isDirtyRef = useRef(false);

  const displayedTime = formatTimecode(timeInSeconds);

  // Leaving edit mode whenever the player is disabled (audio unloaded, track
  // swapped) avoids stranding an input over a player that cannot seek.
  useEffect(() => {
    if (isDisabled) setIsEditing(false);
  }, [isDisabled]);

  useEffect(() => {
    if (!isEditing || !inputRef.current) return;

    inputRef.current.focus();
    inputRef.current.select();
  }, [isEditing]);

  const startEditing = (event) => {
    event.stopPropagation();
    if (isDisabled) return;

    setDraft(displayedTime);
    isDirtyRef.current = false;
    setIsEditing(true);
  };

  const commit = () => {
    const seconds = parseTimecode(draft, durationInSeconds);

    // Only seek on a timecode the user actually typed: committing an untouched
    // field would rewind playback to the start of the current second.
    // An unreadable timecode just closes the field, the playhead stays put.
    if (isDirtyRef.current && seconds !== null) onSeek(seconds);

    setIsEditing(false);
  };

  const onKeyDown = (event) => {
    // The player sits inside clickable cards and modals — keep its keystrokes
    // from bubbling up to them.
    event.stopPropagation();

    if (event.key === "Enter") {
      event.preventDefault();
      commit();
    }

    if (event.key === "Escape") {
      event.preventDefault();
      // Discard the draft so a blur fired while unmounting cannot seek anyway.
      isDirtyRef.current = false;
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        aria-label={t("Aller à un instant précis")}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
          isDirtyRef.current = true;
        }}
        onKeyDown={onKeyDown}
        onBlur={commit}
        onClick={(event) => event.stopPropagation()}
        className={`bg-transparent border-b border-primary outline-none tabular-nums text-center p-0 ${className} ${inputClassName}`}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={startEditing}
      disabled={isDisabled}
      title={t("Cliquer pour aller à un instant précis")}
      className={`bg-transparent tabular-nums p-0 ${
        isDisabled ? "cursor-default" : "cursor-text hover:text-primary hover:underline"
      } ${className}`}
    >
      {displayedTime}
    </button>
  );
};

export default SeekableTime;
